// @givmo/step-up — the prompt's words, as ACTION-KEYED DATA a host composes from each module's own
// rows (the seam agreed with Account Security).
//
// The package holds only the fallback: the payout prompt's own words for an action it does not
// know (AS-WEB3's, titled in D-AS-29's words: "Confirm it's you" everywhere), and the words of the
// sheet's own parts — the passkey arm, the alternatives, the lost links and the code step — which
// are D-AS-21's approved frames, verbatim (B1a–B3d). No line of it says "verify". Every module keeps
// its rows beside its own copy rails — payout's in `apps/charity/src/features/payment/`, Compliance's
// in `packages/compliance` — so no module's word sits in this package, and a row overrides any part
// of the fallback it needs to.
//
// The code step's line is FIXED for every row (B1c): the code completes the same confirmation the
// row's title names, so it needs no words of its own.

import type { StepUpLostFactor, StepUpLostLines, StepUpOwnWayIn } from './step-up-host';

/** What the caller knows that a row's words may name. The refusal itself carries only `action`. */
export interface StepUpContext {
  organizationName?: string;
  /** The organization's sign-in check, in its own words, where a row names it. */
  level?: string;
}

/** Which re-sign-in the prompt is showing, for a row whose line names it (Board's signing rows). */
export interface StepUpReSignInArm {
  /** The person's password, or one of their providers. */
  way: 'password' | 'provider';
  /** The provider's name ("Google"), when `way` is a provider. */
  providerLabel?: string;
  /** Whether an authenticator code follows this step. */
  codeFollows: boolean;
}

/** One action's words. Anything left out is the fallback's. */
export interface StepUpCopyRow {
  title?: string;
  /** The line under the title. */
  description?: (context: StepUpContext) => string;
  /**
   * The line under the title while the person's own way back in shows (their password, or a
   * provider's window) rather than the passkey, where it differs — Board's "…Enter your password,
   * then the code from your authenticator app." Absent: `description`.
   */
  reSignInDescription?: (context: StepUpContext, arm: StepUpReSignInArm) => string;
  /** The foot line beside Cancel: what happens if the person does not confirm. */
  consequence?: string;
  /**
   * The FINAL confirm's label: the code step's button, or the credential step's when no code
   * follows it. Fallback: "Confirm". A credential step a code follows always reads "Continue".
   */
  confirmLabel?: string;
  /** The second-factor step's title, for a host that draws its own step (`renderSecondFactor`). */
  challengeTitle?: string;
  /** What the code authorises, in a host-drawn step: "Enter the six-digit code from … to {challengeAction}." */
  challengeAction?: string;
  /** Said when no way in can be offered here. */
  unavailable?: string;
  /**
   * Said instead, on a prompt only a passkey can answer, when no passkey can be offered here. Not
   * `unavailable`: a row's dead-end line may send the person to sign in again, and a password or
   * provider sign-in does not answer a passkey-only prompt.
   */
  passkeyUnavailable?: string;
  /** The passkey arm's button. */
  passkeyLabel?: string;
  /** The line under the passkey button. */
  passkeyHint?: string;
  /** The line under it while the browser's own prompt is up. */
  passkeyWaiting?: string;
  /**
   * What a lost factor means for this act, by the factor lost, in the view its lost link opens
   * (D-AS-21's B2d-lost; AS-WEB18). Board's signing rows: "You can still read and reply. Signing
   * waits until you've replaced it, …". Any line left out is the host view's own.
   */
  lostLines?: Partial<Readonly<Record<StepUpLostFactor, StepUpLostLines>>>;
}

/** Action → its row. */
export type StepUpCopyTable = Readonly<Record<string, StepUpCopyRow>>;

/** Every line, resolved. */
export interface StepUpWords {
  title: string;
  description: string;
  /** The line while the person's own way in shows. */
  reSignInDescription: (arm: StepUpReSignInArm) => string;
  consequence?: string;
  /** The final confirm's label. */
  confirmLabel: string;
  /** A credential step's label when a code follows it. */
  continueLabel: string;
  passwordLabel: string;
  providerLabel: (provider: string) => string;
  challengeTitle: string;
  challengeLine: (factorDisplayName: string | null) => string;
  back: string;
  cancel: string;
  unavailable: string;
  /**
   * Said instead of `unavailable` on a prompt only a passkey can answer when no passkey can be
   * offered here (the host could not read the account's passkeys): a password or provider sign-in
   * does not answer it, so `unavailable`'s fallback ("sign out, sign in again") would be false there.
   */
  passkeyUnavailable: string;
  passkeyLabel: string;
  passkeyHint: string;
  passkeyWaiting: string;
  /** The passkey failed or was dismissed; `instead` is the person's own way in, when they have one. */
  passkeyFailed: (instead: StepUpOwnWayIn | null) => string;
  /** The link to the person's own way in, beside the passkey. */
  useInstead: (way: StepUpOwnWayIn) => string;
  /** The link back to the passkey, beside the person's own way in. */
  usePasskeyInstead: string;
  lostPasskey: string;
  lostAuthenticator: string;
  /** The code step's line (B1c), the same for every row. */
  codeLine: string;
  /** The code boxes' accessible name. */
  codeLabel: string;
  /** A refused code (B2d-fail): the boxes clear. */
  codeRejected: string;
  /** The row's lines for a lost factor's view (`{}` when it names none): handed to `renderLost`. */
  lostLines: (factor: StepUpLostFactor) => StepUpLostLines;
}

/** The fallback: AS-WEB3's prompt for an action it did not name, titled in D-AS-29's words. */
const FALLBACK = {
  title: 'Confirm it’s you',
  description: () =>
    'For your security, confirm your identity before we save this change. Your details are saved as soon as you do — nothing is lost.',
  confirmLabel: 'Confirm',
  challengeTitle: 'Confirm it’s you',
  challengeAction: 'save this change',
  unavailable:
    'We couldn’t confirm your identity here. Please sign out, sign in again, and retry this change.',
  // D-AS-29 fix 12's approved iPhone line, without its pointer to "Can't use a passkey?": this state
  // has no passkey to offer, so no lost link sits below it (approved, D-AS-35 B8).
  passkeyUnavailable: 'We couldn’t load your passkeys just now. Try again in a moment.',
  passkeyLabel: 'Use your passkey',
  passkeyHint: 'Your fingerprint or face, on this device.',
  passkeyWaiting: 'Waiting for your fingerprint or face…',
} as const;

/** How the person's own way in is named in the sheet's links and failure line. */
function wayName(way: StepUpOwnWayIn): string {
  return way.kind === 'password' ? 'your password' : way.label;
}

/** The words for one prompt: the action's row over the fallback. */
export function stepUpWords(
  table: StepUpCopyTable,
  action: string | null,
  context: StepUpContext = {},
): StepUpWords {
  const row: StepUpCopyRow = (action !== null ? table[action] : undefined) ?? {};
  const challengeAction = row.challengeAction ?? FALLBACK.challengeAction;
  const description = (row.description ?? FALLBACK.description)(context);
  return {
    title: row.title ?? FALLBACK.title,
    description,
    reSignInDescription: (arm) =>
      row.reSignInDescription !== undefined ? row.reSignInDescription(context, arm) : description,
    ...(row.consequence !== undefined ? { consequence: row.consequence } : {}),
    confirmLabel: row.confirmLabel ?? FALLBACK.confirmLabel,
    continueLabel: 'Continue',
    passwordLabel: 'Password',
    providerLabel: (provider) => `Continue with ${provider}`,
    challengeTitle: row.challengeTitle ?? FALLBACK.challengeTitle,
    challengeLine: (factorDisplayName) =>
      factorDisplayName !== null
        ? `Enter the six-digit code from ${factorDisplayName} to ${challengeAction}.`
        : `Enter the six-digit code from your authenticator app to ${challengeAction}.`,
    back: 'Back',
    cancel: 'Cancel',
    unavailable: row.unavailable ?? FALLBACK.unavailable,
    passkeyUnavailable: row.passkeyUnavailable ?? FALLBACK.passkeyUnavailable,
    passkeyLabel: row.passkeyLabel ?? FALLBACK.passkeyLabel,
    passkeyHint: row.passkeyHint ?? FALLBACK.passkeyHint,
    passkeyWaiting: row.passkeyWaiting ?? FALLBACK.passkeyWaiting,
    passkeyFailed: (instead) =>
      instead === null
        ? 'That didn’t work. Try again.'
        : `That didn’t work. Try again, or use ${wayName(instead)} instead.`,
    useInstead: (way) => `Use ${wayName(way)} instead`,
    usePasskeyInstead: 'Use your passkey instead',
    lostPasskey: 'Lost your passkey?',
    lostAuthenticator: 'Lost your authenticator?',
    codeLine: 'Enter the 6-digit code from your authenticator app.',
    codeLabel: '6-digit code',
    codeRejected: 'That code didn’t work. Enter the code your app shows now.',
    lostLines: (factor) => row.lostLines?.[factor] ?? {},
  };
}
